import React, { useEffect, useState } from "react";
import { useCertificateDownload, useStyles, useTranslations } from "examsbook-react-sdk/custom-hooks";
import lock from "../../assets/images/lock.png";
import defaultCertificateImage from "../../assets/images/certificateTemplate.png";
import { useDispatch, useSelector } from "react-redux";
import { courseCertificateListAction } from "examsbook-react-sdk/actions/certificateAddResponse";
import { Typography, Box, Button, useMediaQuery } from "@material-ui/core";
import { useTheme as useMuiTheme } from "@material-ui/core/styles";
import { setUnlockCertificate } from "examsbook-react-sdk/actions/getSelfLearningAction";
import {
  WhatsappIcon, TwitterIcon, FacebookIcon
} from "react-share";

export default function CertificateCard({ sectionData }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const t = useTranslations();
  const userName = user?.user?.name ? user?.user?.name : user?.name;
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const classes = useStyles(styles)();
  const [certificateData, setCertificateData] = useState([]);
  const { handleDownloadCertificate } = useCertificateDownload({ userName });
  const [isLoading, setIsLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const settingsData = useSelector((state) => state.settingsReducers.settingsData?.data);
  const certificateBg = settingsData?.data?.[0]?.branding?.certificateImage?.imagePath
    ? settingsData.data[0].branding.certificateImage.imagePath
    : defaultCertificateImage;

  useEffect(() => {
    if (sectionData?.data?.courseId?._id) {
      dispatch(courseCertificateListAction(sectionData?.data?.courseId?._id)).then((res) => {
        setIsLoading(true);
        if (res?.data?.length) {
          setCertificateData(res?.data[0]);
          dispatch(setUnlockCertificate(res?.data?.[0]?.isCompleted));
        }
      });
    }
  }, [sectionData?.data?.courseId?._id]);

  const isCertCompleted = certificateData && certificateData?.isCompleted === true;
  const courseName = useSelector((state) => state.filteredCourseList.courseName);
  const shareUrl = window.location.href;
  const shareTitle = `🎉 I just completed "${certificateData?.courseId?.name || courseName}" and earned my certificate!`;

  const handleShare = async (platform) => {
    if (sharing) return;
    try {
      setSharing(true);
      const shareWindow = window.open("", "_blank");

      const imageUrl = certificateData?.shareUrl;
      if (!imageUrl) {
        console.warn("Certificate share URL not available yet");
        shareWindow?.close();
        return;
      }

      const text = encodeURIComponent(shareTitle);
      const encodedUrl = encodeURIComponent(imageUrl);
      const links = {
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${text}`,
        whatsapp: `https://api.whatsapp.com/send?text=${text}%20${imageUrl}`,
      };
      if (shareWindow) shareWindow.location.href = links[platform];
    } catch (err) {
      console.error("Share failed", err);
    } finally {
      setSharing(false);
    }
  };

  return (
    <>
      <Box style={{ minHeight: "20px" }}>
        {!isCertCompleted && isLoading ? (
          <Typography className={classes.certifiedText}>
            *{t("Complete the course and pass the evaluation test with")}{" "}
            {sectionData?.data?.evaluationTest[0]?.testId?.passingPercent}%{" "}
            {t("to unlock the certificate")}
          </Typography>
        ) : (
          isLoading && (
            <Typography className={classes.certifiedTextPass}>
              ✅ {t("Well done! You've successfully unlocked your course certificate, and it's ready to download.")}
            </Typography>
          )
        )}
      </Box>

      {/* Certificate Preview - only when unlocked */}
      {isCertCompleted && (
        <Box style={{ margin: "0 auto 12px", position: "relative", display: "block", width: "70%", marginTop: "10px"  }}>
          <img src={certificateBg} alt="Certificate Preview" style={{ width: "100%", borderRadius: "4px", display: "block" }} />
          {/* Name overlay - matches AlexBrush 42px at top:156px on 446px height = ~35% */}
          <Box style={{ position: "absolute", top: "35%", width: "100%", textAlign: "center", padding: "0 4px"}}>
            <Typography style={{ fontFamily: "'AlexBrush', cursive", fontSize: isMobile ? "3.5vw" : "2.8vw", color: "#333", letterSpacing: "0px", lineHeight: 1.2 }}>
              {userName}
            </Typography>
            <Typography style={{ fontFamily: "serif", fontSize: isMobile ? "1.6vw" : "1.2vw", color: "#333", marginTop: "2px", lineHeight: 1.3 }}>
              You have successfully completed <strong>{certificateData?.courseId?.name || courseName}</strong> Course from
            </Typography>
            <Typography style={{ fontFamily: "serif", fontSize: isMobile ? "1.6vw" : "1.2vw", fontWeight: "600", color: "#333", lineHeight: 1.3 }}>
              <strong>LEARNIFY</strong> on {new Date(certificateData?.completedOn).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Download & Share Buttons */}
      {isCertCompleted ? (
        <Box style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: "center", margin: "0 16px 16px", gap: isMobile ? "12px" : "0", position: isMobile ? "static" : "relative" }}>
          <Box style={isMobile ? { width: "100%", display: "flex", justifyContent: "center" } : { position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleDownloadCertificate(certificateData?.courseId?.name, certificateData?.completedOn)}
            >
              {t("Download")}
            </Button>
          </Box>
          <Box style={{ display: "flex", alignItems: "center", gap: "10px", marginLeft: isMobile ? "0" : "auto" }}>
            <Box onClick={() => handleShare("linkedin")} style={{ cursor: sharing ? "not-allowed" : "pointer", borderRadius: "50%", overflow: "hidden", width: 32, height: 32, opacity: sharing ? 0.6 : 1 }}>
              <svg viewBox="0 0 64 64" width="32" height="32"><circle cx="32" cy="32" r="32" fill="#0077b5"/><path d="M20.4 42.6V26.2h-5.4v16.4h5.4zm-2.7-18.6c1.9 0 3-1.2 3-2.8-.1-1.6-1.2-2.8-3-2.8s-3 1.2-3 2.8c0 1.6 1.1 2.8 2.9 2.8h.1zm5.7 18.6h5.4V33.4c0-.5 0-1 .2-1.3.4-1 1.4-2 3-2 2.1 0 3 1.6 3 4v8.5h5.4V33.4c0-5.3-2.8-7.7-6.6-7.7-3.1 0-4.4 1.7-5.2 2.9v.1h-.1l.1-.1v-2.4h-5.4c.1 1.5 0 16.4 0 16.4z" fill="#fff"/></svg>
            </Box>
            <Box onClick={() => handleShare("whatsapp")} style={{ cursor: sharing ? "not-allowed" : "pointer", borderRadius: "50%", overflow: "hidden", width: 32, height: 32, opacity: sharing ? 0.6 : 1 }}>
              <WhatsappIcon size={32} round />
            </Box>
            <Box onClick={() => handleShare("twitter")} style={{ cursor: sharing ? "not-allowed" : "pointer", borderRadius: "50%", overflow: "hidden", width: 32, height: 32, opacity: sharing ? 0.6 : 1 }}>
              <TwitterIcon size={32} round />
            </Box>
            <Box onClick={() => handleShare("facebook")} style={{ cursor: sharing ? "not-allowed" : "pointer", borderRadius: "50%", overflow: "hidden", width: 32, height: 32, opacity: sharing ? 0.6 : 1 }}>
              <FacebookIcon size={32} round />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box className="certificate-text-div" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>


            <svg width="24" height="22" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg" class="jss4532" alt="Certificate"><path id="Vector" d="M12.9492 15.7969V21.1969L15.3492 19.3969L17.7492 21.1969V15.7969M9.34922 17.5969H3.34922C2.7127 17.5969 2.10225 17.344 1.65216 16.8939C1.20208 16.4438 0.949219 15.8334 0.949219 15.1969V3.19688C0.949219 1.87687 2.02922 0.796875 3.34922 0.796875H20.1492C20.7857 0.796875 21.3962 1.04973 21.8463 1.49982C22.2964 1.94991 22.5492 2.56036 22.5492 3.19688V15.1969C22.5488 15.6177 22.4377 16.0311 22.2271 16.3955C22.0164 16.7599 21.7137 17.0624 21.3492 17.2729M4.54922 5.59688H18.9492M4.54922 9.19688H8.14922M4.54922 12.7969H6.94922M11.7492 12.7969C11.7492 13.7517 12.1285 14.6673 12.8036 15.3425C13.4788 16.0176 14.3944 16.3969 15.3492 16.3969C16.304 16.3969 17.2197 16.0176 17.8948 15.3425C18.5699 14.6673 18.9492 13.7517 18.9492 12.7969C18.9492 11.8421 18.5699 10.9264 17.8948 10.2513C17.2197 9.57616 16.304 9.19688 15.3492 9.19688C14.3944 9.19688 13.4788 9.57616 12.8036 10.2513C12.1285 10.9264 11.7492 11.8421 11.7492 12.7969Z" stroke="#0C3450" stroke-width="1.275" stroke-linecap="round" stroke-linejoin="round"></path></svg>
            <Box>
              <Typography style={{ fontSize: "14px", fontWeight: 500, color: "#333" }}>
                {certificateData?.courseId?.name || courseName || sectionData?.data?.courseId?.name}
              </Typography>
              <Typography style={{ fontSize: "12px", color: "#999", marginTop: "2px" }}>
                {t("Complete the course to unlock the certificate")}
              </Typography>
            </Box>
          </Box>
            <img src={lock} alt="Lock icon" style={{ maxWidth: '15px', cursor: 'pointer' }} />

        </Box>
      )}
    </>
  );
}

const styles = {
  certifiedText: {
    color: "#F75151",
    fontSize: "12px",
    fontWeight: 500,
    fontStyle: "italic",
    margin: "12px 16px",
  },
  certifiedTextPass: {
    color: "#2e7d32",
    fontSize: "12px",
    fontWeight: 500,
    fontStyle: "italic",
    margin: "12px 16px",
  },
};
